import {
  BasicButton,
  ButtonGroup,
  GhostButton,
  Link,
  Modal,
  ModalTrigger,
  PrimaryButton,
  Stack,
  Text,
} from "@certn/cascadia";
import { Checkbox, Icon, SectionMessage, Space } from "@certn/cascadia-react";
import { CascadiaLayoutProps } from "@certn/forms";
import { css } from "@emotion/css";
import { JsonSchema } from "@jsonforms/core";
import { useContext, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { NonBiometricDemoSkipModal } from "./NonBiometricDemoSkipModal";
import { NonBiometricIdInfoWeCollectModal } from "./NonBiometricIdInfoWeCollectModal";
import { NonBiometricIdModal } from "./NonBiometricIdModal";
import { NonBiometricIdOptOutModal } from "./NonBiometricIdOptOutModal";
import { InteracSessionIllustration } from "./assets/InteracSessionIllustration";
import { useApplication } from "../../../ApplicationContext";
import Constants from "../../../constants";
import { OneIdLayoutContext } from "../../../layouts/OneIdLayoutContext";
import { getAllowRealResponses } from "../../../utils";
import { useBiometricIdOptOutHandler } from "../../one-id/hooks/useBiometricIdOptOutHandler";
import { useNonBiometricIdOptOutHandler } from "../../one-id/hooks/useNonBiometricIdOptOutHandler";

interface Props {
  schema: JsonSchema;
  data: CascadiaLayoutProps["data"];
  fulfillWithDemoResponses: {
    only_fulfill_with_demo_responses: boolean;
    check_types_allowing_non_demo_responses: string[];
  };
}

export function NonBiometricIdSection(props: Props) {
  const { t } = useTranslation();
  const [showOptOutModal, setShowOptOutModal] = useState(false);

  const [showDemoSkipModal, setShowDemoSkipModal] = useState(false);
  const {
    optedOutOfBiometricId,
    optedOutOfNonBiometricId,
    setShowNonBiometricIdSection,
    idvCheckTypes,
    mutateIdvCheck,
  } = useContext(OneIdLayoutContext);
  const { prevSection, navigateToSection } = useApplication();
  const changeBiometricIdOptOut = useBiometricIdOptOutHandler();
  const { schema, data } = props;
  const changeNonBiometricIdOptOut = useNonBiometricIdOptOutHandler();
  const { refresh } = useApplication();
  const optOutOptions =
    schema?.properties?.non_biometric_id_verification_opt_out?.properties
      ?.opt_out_reason.oneOf;

  const demoInteracCode = "12345";

  const hasCompletedNonBio = data?.applicant_form_data.interac_code;

  const hasNonBioCheck = idvCheckTypes?.includes("NON_BIOMETRIC_ID_CANADA");

  const allowRealResponses = getAllowRealResponses(
    props.fulfillWithDemoResponses,
    ["NON_BIOMETRIC_ID_CANADA_1"]
  );
  const optBackInToBiometricId = async () => {
    await changeBiometricIdOptOut(false);
    setShowNonBiometricIdSection(false);
  };

  useEffect(() => {
    if (!hasNonBioCheck) {
      mutateIdvCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNonBioCheck]);

  return (
    <Stack gap="space-6">
      <Stack gap="space-2">
        <Text
          as="h1"
          style="form/page-title"
          testID="identity-verification-section-title"
        >
          {t("Non-Biometric Identity Verification")}
        </Text>
        <Text
          style="form/body-primary"
          color="text-weak"
          testID="non-biometric-identity-verification-section-subtitle"
        >
          {!optedOutOfBiometricId && (
            <Trans>
              Because your identity verification was unsuccessful we will need
              to use an alternative identity verification option to continue
              your background check.
            </Trans>
          )}
          {optedOutOfBiometricId && (
            <Trans>
              Because you have opted out of Biometric Data Collection we will
              need to use an alternative identity verification option to
              continue your background check.
            </Trans>
          )}
        </Text>
      </Stack>
      {!optedOutOfNonBiometricId && (
        <Stack gap="space-2">
          <SectionMessage
            type="success"
            sectionTitle={t("Your privacy is important to us")}
            icon="verified_user"
          >
            <Trans>
              Certn has partnered with Interac® verification service to
              securely verify your identity by establishing a connection with
              your financial institution through your existing login
              credentials. Information we collect and share in the report only
              includes basic information about you.{" "}
              <ModalTrigger
                trigger={() => (
                  <Link linkStyle="monochrome" testID="privacy-policy-link">
                    {t("Learn more")}
                  </Link>
                )}
              >
                <Modal testID="non-biometric-id-info-we-collect-modal">
                  <NonBiometricIdInfoWeCollectModal />
                </Modal>
              </ModalTrigger>
            </Trans>
          </SectionMessage>
        </Stack>
      )}
      <Stack gap="space-2">
        <div className={styles.interacIllustrationContainer}>
          <InteracSessionIllustration />
        </div>
      </Stack>
      {optedOutOfBiometricId &&
        !optedOutOfNonBiometricId &&
        !hasCompletedNonBio && (
          <Stack gap="space-2">
            <Trans>
              <Text style="form/body-secondary" color="text-weak">
                <Link
                  testID="non-biometric-id-alternative-verification-link"
                  textStyle="global/body-secondary"
                  textColor="text-weak"
                  onPress={optBackInToBiometricId}
                >
                  Click here
                </Link>{" "}
                if you would like to switch back to the previous verification
                method.
              </Text>
            </Trans>
          </Stack>
        )}
      <Stack gap="space-2">
        {optedOutOfNonBiometricId && (
          <Text
            style="form/body-secondary"
            color="text-warning"
            className={styles.textItemWrapper}
          >
            <Icon fill name="warning" color="text-warning" />
            {t("You have chosen to skip this step.")}
            <Link
              testID="non-biometric-id-alternative-verification-link"
              textStyle="global/body-secondary"
              textColor="text-warning"
              onPress={() => {
                changeNonBiometricIdOptOut("", "");
              }}
            >
              {t("Undo")}
            </Link>
          </Text>
        )}
        {!optedOutOfNonBiometricId && !hasCompletedNonBio && (
          <Checkbox
            isSelected={optedOutOfNonBiometricId}
            onChange={() => {
              setShowOptOutModal(true);
            }}
            testID="non-biometric-opt-out"
            description={t("You will be required to give a reason why")}
          >
            {t("I can’t complete verification via my bank")}
          </Checkbox>
        )}
        <NonBiometricIdOptOutModal
          optOutChoices={optOutOptions}
          show={showOptOutModal}
          onClose={() => {
            setShowOptOutModal(false);
          }}
        />
      </Stack>
      <div className={css(styles.actionBar)}>
        <ButtonGroup>
          {prevSection && (
            <GhostButton
              testID="section-back-button"
              leftIcon="chevron_left"
              onPress={async () => {
                navigateToSection(prevSection);
              }}
            >
              {t("Back")}
            </GhostButton>
          )}
          <Space />
          {(optedOutOfNonBiometricId || hasCompletedNonBio) && (
            <PrimaryButton testID="next-step-button" type="submit">
              {t("Next step")}
            </PrimaryButton>
          )}
          {!optedOutOfNonBiometricId && (
            <ButtonGroup>
              <>
                {Constants.enableDemoResponses && !hasCompletedNonBio && (
                  <BasicButton onPress={() => setShowDemoSkipModal(true)}>
                    {t("Demo Response")}
                  </BasicButton>
                )}
              </>
              {allowRealResponses && !hasCompletedNonBio && (
                <ModalTrigger
                  trigger={() => (
                    <PrimaryButton
                      testID="start-verification-with-interac-button"
                      isDisabled={!hasNonBioCheck}
                    >
                      {t("Start verification")}
                    </PrimaryButton>
                  )}
                >
                  <Modal testID="non-biometric-id-modal">
                    <NonBiometricIdModal
                      optBackInToBiometricId={optBackInToBiometricId}
                    />
                  </Modal>
                </ModalTrigger>
              )}
            </ButtonGroup>
          )}
        </ButtonGroup>
      </div>
      {Constants.enableDemoResponses && (
        <NonBiometricDemoSkipModal
          demoInteracCode={demoInteracCode}
          isOpen={showDemoSkipModal}
          onClose={() => setShowDemoSkipModal(false)}
          onSave={async () => {
            refresh();
            setShowDemoSkipModal(false);
          }}
        />
      )}
    </Stack>
  );
}

const styles = {
  actionBar: css({
    paddingTop: "calc(var(--space-4) - 1px)",
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
    borderTop: "1px solid var(--border-base)",
  }),
  iconStyles: css({ marginRight: "var(--space-2)" }),
  textItemWrapper: css({
    display: "flex",
    gap: "var(--space-1)",
    marginTop: "var(--space-05)",
    marginBottom: "var(--space-05)",
  }),
  interacIllustrationContainer: css({
    display: "flex",
    flex: 1,
    justifyContent: "center",
    borderRadius: "var(--border-radius-2)",
    backgroundColor: "var(--background-accent-neutral-weak)",
    paddingLeft: "var(--space-4)",
    paddingRight: "var(--space-4)",
  }),
};
